// Small building blocks shared by the Blog list and the BlogEditor.

import type { CSSProperties, ReactNode } from 'react';
import { STATUS_COLOR, STATUS_LABEL, type PostStatus } from '@/lib/blog';

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      style={{ display: 'inline-flex', gap: 4, background: 'var(--surface-2)', padding: 4, borderRadius: 10, flexWrap: 'wrap' }}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.value)}
            style={{
              padding: '6px 14px',
              borderRadius: 8,
              border: 'none',
              background: active ? 'var(--surface)' : 'transparent',
              color: active ? 'var(--ink)' : 'var(--ink-3)',
              fontSize: 13,
              fontWeight: active ? 600 : 500,
              fontFamily: 'var(--font-ui)',
              cursor: 'pointer',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function StatusBadge({ status }: { status: PostStatus }) {
  return (
    <span
      className="mono"
      style={{
        fontSize: 10,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        color: STATUS_COLOR[status],
        border: `1px solid ${STATUS_COLOR[status]}`,
        borderRadius: 6,
        padding: '1px 6px',
        whiteSpace: 'nowrap',
      }}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

export function ErrorBanner({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', padding: '8px 12px', borderRadius: 10, fontSize: 13 }}
    >
      {children}
    </div>
  );
}

export function FieldError({ message }: { message?: string | null }) {
  if (!message) return null;
  return (
    <div role="alert" style={{ color: '#a8331f', fontSize: 12, marginTop: 4 }}>
      {message}
    </div>
  );
}

export function FieldLabel({ children, htmlFor, aside }: { children: ReactNode; htmlFor?: string; aside?: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
      <label htmlFor={htmlFor} style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)' }}>
        {children}
      </label>
      {aside}
    </div>
  );
}

export const inputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--line-strong)',
  borderRadius: 8,
  fontSize: 14,
  fontFamily: 'var(--font-ui)',
  background: 'var(--surface)',
  color: 'var(--ink)',
};

export const primaryButton: CSSProperties = {
  padding: '8px 18px',
  background: 'var(--coral)',
  color: '#fff',
  border: 'none',
  borderRadius: 100,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: 'var(--font-ui)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

export const secondaryButton: CSSProperties = {
  padding: '7px 14px',
  background: 'transparent',
  color: 'var(--ink-2)',
  border: '1px solid var(--line-strong)',
  borderRadius: 100,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: 'var(--font-ui)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

export const dangerButton: CSSProperties = {
  ...secondaryButton,
  color: '#a8331f',
  borderColor: 'rgba(168,51,31,0.35)',
};

export function disabledStyle(disabled: boolean): CSSProperties {
  return disabled ? { opacity: 0.5, cursor: 'default' } : {};
}

export function Counter({ n, max, min }: { n: number; max: number; min?: number }) {
  const bad = n > max || (min !== undefined && n > 0 && n < min);
  return (
    <span className="mono" style={{ fontSize: 11, color: bad ? '#a8331f' : 'var(--ink-4)' }}>
      {n}/{max}
    </span>
  );
}
